import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, Metrics } from '../../themes';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Select from '../common/Select';
import Button from '@material-ui/core/Button';
import SvgIcon from '../../icons/SvgIcon';
import Table from '../common/Table';
import DetailsTableHeader from '../common/DetailsTableHeader';

const useStyles = makeStyles((theme) => {
  return {
    DetailsWidget: {
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
    },
    headerButtonsContainer: {
      background: Colors.navBackgroundGrey,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
      padding: 10,
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: 6,
        justifyContent: 'flex-start',
      },
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
        '&:hover': {
          background: Colors.black,
        },
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 6,
      paddingRight: 6,
    },
    saveButton: {
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    deleteButton: {
      margin: 10,
      marginRight: 'auto',
      minWidth: 38,
      width: 38,
      maxWidth: 38,
      padding: 0,
      borderColor: Colors.red,
      '&:hover': {
        background: Colors.red,
      },
      [theme.breakpoints.down(Metrics.minWidth)]: {
        marginRight: 8,
      },
      '&:disabled:hover': {
        background: Colors.black,
      },
    },
    // details
    detailsContainer: {
      padding: '25px 20px',
      [theme.breakpoints.down('xs')]: {
        padding: 15,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    formContainer: {
      alignSelf: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    errorDisplay: {
      margin: '20px 10px',
      color: Colors.errorRed,
    },
  };
});

const DoorControlDetails = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { width } = props.size;
  const isSmall = width < Metrics.formDetails.small;
  const formInputWidth = isSmall ? width - 40 : (width - 60) / 2;
  const {
    deleteButtonText,
    add1ButtonText,
    onDeleteButtonClick,
    onAdd1ButtonClick,
    onSaveButtonClick,
    isDeleteButtonDisabled,
    onChange,
    hideDetails,
    noDetailsTable,
    name,
    schedulesSelectValue,
    checkedDoors,
    isNewDoorControl,
    isUntouched,
    setUntouched,
  } = props;
  const smallerThanMedium = useMediaQuery(`(max-width: ${Metrics.breakpoints.md}px)`);
  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveButtonClick();
  };
  const [isNameError, setNameError] = useState(false);
  const [nameHelperText, setNameHelperText] = useState(null);
  const [isNameTouched, setNameTouched] = useState(false);
  const [isSchedulesError, setSchedulesError] = useState(false);
  const [schedulesHelperText, setSchedulesHelperText] = useState(null);
  const [isSchedulesTouched, setSchedulesTouched] = useState(false);
  const [doorsHelperText, setDoorsHelperText] = useState(null);
  const [isFormValid, setFormValid] = useState(false);

  const setFieldsTouched = useCallback(
    (value) => {
      setNameTouched(value);
      setSchedulesTouched(value);
      setUntouched(false);
    },
    [setUntouched]
  );

  useEffect(() => {
    const validate = () => {
      const isNameValid = name !== '';
      const isSchedulesValid = schedulesSelectValue !== '';
      const isDoorsValid = !isNewDoorControl || (isNewDoorControl && checkedDoors.length > 0);
      if (
        (!isNameValid && !isNameTouched) ||
        (!isSchedulesValid && !isSchedulesTouched) ||
        (!isDoorsValid && (isNameTouched || isSchedulesTouched))
      ) {
        setFormValid(false);
      }
      if (!isNameValid && isNameTouched) {
        setNameError(true);
        setNameHelperText(t('validation.requiredField'));
        setFormValid(false);
      }
      if (!isSchedulesValid && isSchedulesTouched) {
        setSchedulesError(true);
        setSchedulesHelperText(t('validation.requiredField'));
        setFormValid(false);
      }
      if (!isDoorsValid && (isNameTouched || isSchedulesTouched)) {
        setDoorsHelperText(t('doorControlManager_updateError2'));
      }
      if (isNameValid) {
        setNameError(false);
        setNameHelperText(null);
      }
      if (isSchedulesValid) {
        setSchedulesError(false);
        setSchedulesHelperText(null);
      }
      if (isDoorsValid) {
        setDoorsHelperText(null);
      }
      if (isNameValid && isSchedulesValid && isDoorsValid) {
        setFormValid(true);
      }
    };
    validate();
  }, [name, schedulesSelectValue, isNameTouched, isSchedulesTouched, checkedDoors, isNewDoorControl, t]);

  useEffect(() => {
    if (isUntouched) {
      setFieldsTouched(false);
      setNameError(false);
      setNameHelperText(null);
      setSchedulesError(false);
      setSchedulesHelperText(null);
      setDoorsHelperText(null);
    }
  }, [isUntouched, setFieldsTouched]);
  const handleBlur = (event) => {
    const { name } = event.target;
    if (name === 'name') {
      setNameTouched(true);
    }
    if (name === 'schedules') {
      setSchedulesTouched(true);
    }
    if (name === 'name' || name === 'schedules') {
      setUntouched(false);
    }
  };

  const errorDisplay = doorsHelperText && <div className={classes.errorDisplay}>{doorsHelperText}</div>;

  return (
    <div className={classes.DetailsWidget}>
      <form id="detailsForm" noValidate autoComplete="off" onSubmit={handleSubmit}>
        <div className={classes.headerButtonsContainer}>
          <Button
            type="button"
            className={clsx(classes.deleteButton, isSmall && classes.smallSizeButton)}
            variant="outlined"
            aria-label={deleteButtonText}
            onClick={onDeleteButtonClick}
            disabled={isDeleteButtonDisabled}
          >
            <SvgIcon
              icon={'Trash'}
              color={isDeleteButtonDisabled ? Colors.disabledGrey : Colors.white}
              stroke={2}
              width={14}
            />
          </Button>
          <Button
            type="button"
            variant="outlined"
            className={clsx(classes.button, isSmall && classes.smallSizeButton)}
            onClick={onAdd1ButtonClick}
            endIcon={<SvgIcon icon="Add" width={smallerThanMedium ? 16 : 20} color={Colors.white} />}
          >
            {add1ButtonText}{' '}
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={clsx(classes.button, classes.saveButton, isSmall && classes.smallSizeButton)}
            endIcon={
              <SvgIcon
                icon="Save"
                width={smallerThanMedium ? 16 : 20}
                color={!isFormValid ? Colors.disabledGrey : Colors.white}
              />
            }
            disabled={!isFormValid}
            onMouseEnter={() => setFieldsTouched(true)}
          >
            {t('Global_save')}
          </Button>
        </div>
        {!hideDetails && (
          <div className="bodyComponent">
            <div className={classes.detailsContainer}>
              <Typography variant="h2">{t('doorControlManager_dControlInfo_header').toUpperCase()}</Typography>
              <div className={classes.formContainer}>
                <TextField
                  style={{ width: formInputWidth }}
                  id="name"
                  autoComplete="off"
                  error={isNameError}
                  helperText={nameHelperText}
                  label={t('doorControlManager_dControlInfo_name')}
                  name="name"
                  margin="normal"
                  value={props.name}
                  onChange={onChange}
                  onBlur={handleBlur}
                  required
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="description"
                  label={t('doorControlManager_dControlInfo_description')}
                  name="description"
                  margin="normal"
                  value={props.description}
                  onChange={onChange}
                />
                <Select
                  error={isSchedulesError}
                  helperText={schedulesHelperText}
                  className={classes.select}
                  selectWidth={formInputWidth}
                  selectData={props.schedulesSelectData}
                  selectLabel={props.schedulesSelectLabel}
                  selectValue={props.schedulesSelectValue}
                  onSelect={props.schedulesOnSelect}
                  accessValue={props.schedulesAccessValue}
                  accessLabel={props.schedulesAccessLabel}
                  required
                />
              </div>
            </div>
          </div>
        )}
      </form>
      {!hideDetails && !noDetailsTable && (
        <>
          <DetailsTableHeader {...props} {...(doorsHelperText && { errorButton: true })} />
          <Table {...props} />
          {errorDisplay}
        </>
      )}
    </div>
  );
};

export default withSize()(DoorControlDetails);
